import { API_URL } from "./../utils/config"
import {
  createHttpLink,
  ApolloClient,
  ApolloLink,
  InMemoryCache,
} from "@apollo/client"
import { onError } from "@apollo/client/link/error"
import { message } from "antd"
import asyncLocalStorage from "../utils/asyncLocalStorage"
import { setContext } from "@apollo/client/link/context"
import isValidJwt from "../utils/isValidJwt"
import history from "./history"
import i18n from "../services/i18n"

const httpLink = createHttpLink({
  uri: API_URL,
})

const errorLink = onError(({ graphQLErrors, networkError }) => {
  if (networkError && "statusCode" in networkError && (networkError as any).statusCode === 401) {
    const isLogoutRoute = window.location.pathname.includes("/logout") || window.location.pathname.includes("/login")
    if (!isLogoutRoute) {
      const responseData = (networkError as any).result
      const customMsg = responseData?.message || i18n.t("sessionEndedOrAccessBlocked")
      message.error({ content: customMsg, key: "session_expired_toast", duration: 4 })
    }
    localStorage.clear()
    setTimeout(() => {
      window.location.href = "/login"
    }, 500)
  }
})

const authLink = setContext(async ({ operationName }, { headers }) => {
  // get the authentication token from local storage if it exists
  let token = await asyncLocalStorage.getItem("accessToken")
  const isRefreshing = operationName === "renewAccessToken"

  if (!isValidJwt(token) && !isRefreshing) {
    const refreshToken = await asyncLocalStorage.getItem("refreshToken")
    if (!refreshToken) return
    const data = await fetch(API_URL, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        operationName: "renewAccessToken",
        query: `
        mutation renewAccessToken($accessToken: String!, $refreshToken: String!) {
          renewAccessToken(renewToken: {
            accessToken: $accessToken,
            refreshToken: $refreshToken
          }) {
            accessToken,
            refreshToken
          }
        }`,
        variables: {
          accessToken: token,
          refreshToken: refreshToken,
        },
      }),
    })

    const dataJson = await data.json()

    if (!dataJson?.data?.renewAccessToken?.accessToken) {
      await asyncLocalStorage.removeItem("accessToken")
      await asyncLocalStorage.removeItem("refreshToken")
      token = null
      history.push("/logout")
    }

    if (dataJson?.data?.renewAccessToken?.accessToken) {
      await asyncLocalStorage.setItem(
        "accessToken",
        dataJson?.data?.renewAccessToken?.accessToken
      )
      await asyncLocalStorage.setItem(
        "refreshToken",
        dataJson?.data?.renewAccessToken.refreshToken
      )
      token = dataJson?.data?.renewAccessToken.accessToken
    }
  }

  // return the headers to the context so httpLink can read them
  return {
    headers: {
      ...headers,
      Authorization: token ? `Bearer ${token}` : "",
    },
  }
})

const client = new ApolloClient({
  link: ApolloLink.from([errorLink, authLink.concat(httpLink)]),
  cache: new InMemoryCache(),
  defaultOptions: {
    watchQuery: {
      fetchPolicy: "network-only",
    },
    query: {
      fetchPolicy: "network-only",
    },
  },
})

export default client
